import { useState, useEffect, useRef } from "react";

export const useChangeTracking = (
  initialWrapperData: any,
  initialProductList: any[], // Now expecting an array of products
) => {
  const [hasWrapperChanges, setHasWrapperChanges] = useState(false);
  const [hasProductChanges, setHasProductChanges] = useState(false);
  const [currentWrapperData, setCurrentWrapperData] = useState<any>(null);
  const [currentProductList, setCurrentProductList] = useState<any[]>([]);

  const initialWrapperRef = useRef<any>(null);
  const initialProductListRef = useRef<any[]>([]);

  // Store initial data when it loads
  useEffect(() => {
    if (initialWrapperData && !initialWrapperRef.current) {
      initialWrapperRef.current = { ...initialWrapperData };
      setCurrentWrapperData({ ...initialWrapperData });
    }
  }, [initialWrapperData]);

  // Store initial product list when it loads
  useEffect(() => {
    if (
      initialProductList &&
      initialProductList.length > 0 &&
      initialProductListRef.current.length === 0
    ) {
      // Store deep copy of initial product list
      initialProductListRef.current = JSON.parse(
        JSON.stringify(initialProductList),
      );
      setCurrentProductList(JSON.parse(JSON.stringify(initialProductList)));
      console.log(
        "Initial product list stored:",
        initialProductListRef.current,
      );
    }
  }, [initialProductList]);

  // Track wrapper changes
  const trackWrapperChange = (field: string, value: any) => {
    setCurrentWrapperData((prev: any) => {
      const newData = { ...prev, [field]: value };
      const hasChanges = checkWrapperChanges(newData);
      setHasWrapperChanges(hasChanges);
      return newData;
    });
  };

  // ✅ Check if wrapper data has changed (with tags special handling)
  const checkWrapperChanges = (newData: any) => {
    if (!initialWrapperRef.current) return false;

    const hasChanges = Object.keys(newData).some((key) => {
      if (key === "version" || key === "created" || key === "updated")
        return false;
      
      // ✅ tags field සඳහා special handling
      if (key === "tags") {
        const initialTags = initialWrapperRef.current[key] || "";
        const newTags = newData[key] || "";
        
        // Both could be string or array, normalize for comparison
        const normalizeTags = (tags: any) => {
          if (!tags) return [];
          if (typeof tags === 'string') {
            return tags.split(',').filter((t: string) => t.trim() !== '');
          }
          if (Array.isArray(tags)) {
            return tags;
          }
          if (typeof tags === 'object') {
            return Object.values(tags);
          }
          return [];
        };
        
        const normalizedInitial = normalizeTags(initialTags);
        const normalizedNew = normalizeTags(newTags);
        
        return JSON.stringify(normalizedInitial) !== JSON.stringify(normalizedNew);
      }
      
      return (
        JSON.stringify(initialWrapperRef.current[key]) !==
        JSON.stringify(newData[key])
      );
    });
    return hasChanges;
  };

  // Track product changes - updates the entire product list
  const trackProductChange = (
    field: string,
    value: any,
    productId?: string,
  ) => {
    if (productId) {
      // Update the specific product in the list
      setCurrentProductList((prev) => {
        const newList = prev.map((product) =>
          product.id === productId ? { ...product, [field]: value } : product,
        );

        // Check if any product has changed
        const hasChanges = checkProductListChanges(newList);
        setHasProductChanges(hasChanges);

        return newList;
      });
    } else {
      // For main product (if needed)
      setHasProductChanges(true);
    }
  };

  // Check if product list has changed by comparing with initial list
  const checkProductListChanges = (newList: any[]) => {
    if (!initialProductListRef.current.length) return false;

    // Compare each product
    for (let i = 0; i < newList.length; i++) {
      const currentProduct = newList[i];
      const initialProduct = initialProductListRef.current.find(
        (p) => p.id === currentProduct.id,
      );

      if (!initialProduct) continue;

      // Check each field for changes (ignore metadata fields)
      const hasProductChanges = Object.keys(currentProduct).some((key) => {
        if (
          key === "version" ||
          key === "created" ||
          key === "updated" ||
          key === "itemWrapperId"
        ) {
          return false;
        }
        return (
          JSON.stringify(initialProduct[key]) !==
          JSON.stringify(currentProduct[key])
        );
      });

      if (hasProductChanges) {
        console.log(`Product ${currentProduct.id} has changes`);
        return true;
      }
    }

    return false;
  };

  // Reset change tracking after save
  const resetChanges = () => {
    if (currentWrapperData) {
      initialWrapperRef.current = { ...currentWrapperData };
    }
    if (currentProductList.length > 0) {
      initialProductListRef.current = JSON.parse(
        JSON.stringify(currentProductList),
      );
    }
    setHasWrapperChanges(false);
    setHasProductChanges(false);
    console.log("Change tracking reset");
  };

  // Get changed wrapper data (only fields that changed)
  const getChangedWrapperData = () => {
    if (!initialWrapperRef.current || !currentWrapperData) return null;

    const changedData: any = {};
    Object.keys(currentWrapperData).forEach((key) => {
      if (key === "version" || key === "created" || key === "updated") return;

      // ✅ Special handling for tags
      if (key === "tags") {
        const initialTags = initialWrapperRef.current[key] || "";
        const newTags = currentWrapperData[key] || "";
        
        const normalizeTags = (tags: any) => {
          if (!tags) return [];
          if (typeof tags === 'string') {
            return tags.split(',').filter((t: string) => t.trim() !== '');
          }
          if (Array.isArray(tags)) {
            return tags;
          }
          if (typeof tags === 'object') {
            return Object.values(tags);
          }
          return [];
        };
        
        const normalizedInitial = normalizeTags(initialTags);
        const normalizedNew = normalizeTags(newTags);
        
        if (JSON.stringify(normalizedInitial) !== JSON.stringify(normalizedNew)) {
          changedData[key] = currentWrapperData[key];
        }
        return;
      }

      if (
        JSON.stringify(initialWrapperRef.current[key]) !==
        JSON.stringify(currentWrapperData[key])
      ) {
        changedData[key] = currentWrapperData[key];
      }
    });
    return Object.keys(changedData).length > 0 ? changedData : null;
  };

  // Get all changed products with their changes
  const getAllChangedProducts = () => {
    if (!initialProductListRef.current.length || !currentProductList.length) {
      return null;
    }
    const changedProducts: any = {};

    currentProductList.forEach((currentProduct) => {
      const initialProduct = initialProductListRef.current.find(
        (p) => p.id === currentProduct.id,
      );

      if (!initialProduct) return;

      const productChanges: any = {};

      Object.keys(currentProduct).forEach((key) => {
        // Skip metadata fields
        if (
          key === "version" ||
          key === "created" ||
          key === "updated" ||
          key === "itemWrapperId"
        ) {
          return;
        }

        if (
          JSON.stringify(initialProduct[key]) !==
          JSON.stringify(currentProduct[key])
        ) {
          productChanges[key] = currentProduct[key];
        }
      });

      if (Object.keys(productChanges).length > 0) {
        changedProducts[currentProduct.id] = productChanges;
      }
    });

    console.log("Changed products:", changedProducts);
    return Object.keys(changedProducts).length > 0 ? changedProducts : null;
  };

  // Get single product changes (for backward compatibility)
  const getChangedProductData = () => {
    const allChanges = getAllChangedProducts();
    if (!allChanges) return null;

    // If there's only one product and it's the main one, return its changes
    const productIds = Object.keys(allChanges);
    if (productIds.length === 1) {
      return allChanges[productIds[0]];
    }
    return allChanges;
  };

  // Get changes for a specific product
  const getProductChanges = (productId: string) => {
    const allChanges = getAllChangedProducts();
    return allChanges ? allChanges[productId] : null;
  };

  return {
    hasWrapperChanges,
    hasProductChanges,
    trackWrapperChange,
    trackProductChange,
    resetChanges,
    getChangedWrapperData,
    getChangedProductData,
    getProductChanges,
    getAllChangedProducts,
    currentWrapperData,
    currentProductList,
  };
};